import type { Session } from 'next-auth';

import { getAppConfig } from '@services/config';

import type { TagCommanderType, TrackingType } from '@app-types/tracking';
import type { UserType } from '@app-types/user';

import { isServer } from '@helpers/config';
import { localApiPaths } from '@helpers/path';

type QueuedEvent = {
    payload: Parameters<TagCommanderType['send']>[0];
    enqueuedAt: number;
};

const SESSION_TIMEOUT_MS = 3000;
const RETRY_COOLDOWN_MS = 30000;
const MAX_QUEUE_SIZE = 100;
const MAX_EVENT_AGE_MS = 5 * 60 * 1000;

class TrackingManager {
    private appConfig = getAppConfig();
    private tag?: TagCommanderType;
    private configured = false;
    private flight?: Promise<void>;
    private retryAfter = 0;
    private queue: QueuedEvent[] = [];

    // One flight covers initialization AND draining: concurrent callers cannot reorder sends.
    // A pending tag.setup is deliberately not raced against a timeout: it cannot be cancelled.
    public setup(): Promise<void> {
        if (typeof window === 'undefined' || !this.appConfig.tracking.enabled) return Promise.resolve();
        if (this.flight) return this.flight;
        if (Date.now() < this.retryAfter && !(this.configured && this.hasTransport())) return Promise.resolve();

        this.flight = this.initializeAndDrain()
            .catch(() => {
                this.retryAfter = Date.now() + RETRY_COOLDOWN_MS;
                console.warn('Tracking initialization unavailable; queued events retained.');
            })
            .finally(() => {
                this.flight = undefined;
                // Events enqueued after the drain loop or during cooldown would otherwise wait for the next hit.
                // Without transport, TrackingScript's onReady triggers the next drain.
                this.pruneQueue();
                if (this.queue.length && (Date.now() < this.retryAfter || this.hasTransport())) {
                    setTimeout(() => void this.setup(), Math.max(0, this.retryAfter - Date.now()));
                }
            });
        return this.flight;
    }

    public async sendTracking(isRenewal: boolean, data: TrackingType) {
        if (typeof window === 'undefined') {
            throw new Error('tracking must be sent from the browser');
        }
        if (!this.appConfig.tracking.enabled) return;

        try {
            // Snapshot at enqueue time, before any await (including URL and nested data).
            const payload: QueuedEvent['payload'] = JSON.parse(
                JSON.stringify({
                    site: data.site ?? this.getSite(),
                    univers: data.universe ?? 'Boutique_mobile',
                    parcours: data.journey ?? (isRenewal ? 'Renouvellement' : 'Acquisition'),
                    url: data.url ?? window.location.href,
                    page: data.page,
                    pageCategorie: data.pageCategory,
                    actionClic: data.action,
                    categorieClic: data.category,
                    nomClic: data.name,
                    ligneMarche: data.marketLine,
                    activiteB2BouB2C: data.isB2B ? 'B2B' : 'B2C',
                    etapeEcommerce: data.eCommerceStep,
                    pageVariante: data.variant,
                    variablesSpecifiques: data.variables,
                    produits: data.products,
                    panier: data.panier,
                }),
            );
            this.pruneQueue();
            // Drop the newest rather than the oldest: the head is usually the page view.
            if (this.queue.length >= MAX_QUEUE_SIZE) {
                console.warn('Tracking queue full; event discarded.');
            } else {
                this.queue.push({ payload, enqueuedAt: Date.now() });
            }
        } catch {
            console.warn('Tracking event could not be serialized; event discarded.');
            return;
        }
        await this.setup();
    }

    private async getUser(): Promise<UserType | undefined> {
        const controller = new AbortController();
        const timeout = setTimeout(() => controller.abort(), SESSION_TIMEOUT_MS);
        try {
            const response = await fetch(localApiPaths.authentification.session, { signal: controller.signal });
            if (!response.ok) throw new Error('Session unavailable');
            const session: Session | null = await response.json();
            return session?.user as UserType | undefined;
        } catch {
            // Never log errors/payloads: they may contain OAuth credentials or personal data.
            console.warn('Tracking session unavailable; continuing anonymously.');
            return undefined;
        } finally {
            clearTimeout(timeout);
        }
    }

    private async initializeAndDrain() {
        if (!this.tag) {
            this.tag = (await import('@bytel/bytel-tag-commander')).default as TagCommanderType;
        }
        const tag = this.tag;
        for (let attempt = 1; !this.configured && attempt <= 3; attempt++) {
            try {
                const user = await this.getUser();
                await tag.setup({
                    host: this.appConfig.tracking.host,
                    debug: this.appConfig.tracking.debug,
                    env: this.appConfig.tracking.env,
                    site: this.getSite(),
                    oauth: user
                        ? {
                              id_personne: user.sub,
                              user_type: user.user_type,
                              accessToken: user.accessToken,
                          }
                        : {},
                    shouldEmitEvents: true,
                    hasHistory: true,
                });

                this.configured = true;
            } catch {
                if (attempt === 3) throw new Error('Tracking setup failed');
                await this.delay(500 * attempt);
            }
        }

        // TrackingScript owns script loading and calls setup() on ready; the vendor owns consent.
        if (!this.hasTransport()) return;
        this.retryAfter = 0;
        this.pruneQueue();
        while (this.queue.length) {
            if (!this.hasTransport()) throw new Error('Tracking transport unavailable');
            const event = this.queue.shift();
            if (!event) break;
            try {
                await tag.send(event.payload);
            } catch {
                // The library can emit in finally and then reject: replay could duplicate
                // the event. Discard only this attempted event, not the remaining queue.
                console.warn('Tracking send failed; delivery unknown, event not replayed.');
            }
            this.pruneQueue();
        }
    }

    private hasTransport() {
        return typeof (window as Window & { tc_events_2?: unknown }).tc_events_2 === 'function';
    }

    private pruneQueue() {
        const cutoff = Date.now() - MAX_EVENT_AGE_MS;
        this.queue = this.queue.filter((event) => event.enqueuedAt > cutoff);
    }

    private delay(ms: number): Promise<void> {
        return new Promise((resolve) => setTimeout(resolve, ms));
    }

    private getSite() {
        if (isServer()) {
            throw new Error('Could not get tracking site from server side');
        }

        const bankCartData = localStorage.getItem('yama_cart');
        const isActiveBankCart =
            bankCartData !== null && Number(JSON.parse(bankCartData)?.expiresTimestamp) > Date.now();
        return isActiveBankCart ? 'Partenaire_cfcm_boutique' : 'Boutique';
    }
}

export const trackingManager = new TrackingManager();
void trackingManager.setup();
